import type { ReactNode, RefObject, MouseEvent as ReactMouseEvent } from 'react';
import { useState } from 'react';
import { Popup, FlowForm, getKeyHandler } from 't-lib-ui/structure';
import type { PostRendererHandle } from './PostRenderer';
import DropdownIcon from './DropdownIcon';
import ChevronRightIcon from './ChevronRightIcon';
import ArrowRightIcon from './ArrowRightIcon';
import FeedSettingsBadges from './FeedSettingsBadges';
import HomeIcon from './HomeIcon';
import ChevronLeftIcon from './ChevronLeftIcon';
import HamburgerIcon from './HamburgerIcon';
import TwoColumnIcon from './TwoColumnIcon';
import SingleColumnIcon from './SingleColumnIcon';
import ListIcon from './ListIcon';
import UnfollowIcon from './UnfollowIcon';
import FlagIcon from './FlagIcon';
import SkipFeedIcon from './SkipFeedIcon';
import ShowAllIcon from './ShowAllIcon';
import UndoIcon from './UndoIcon';
import SettingsIcon from './SettingsIcon';
import { menuKeyDown, menuBlur } from './menuUtils';

interface PostViewHeaderProps {
  // Feed identity
  feedName?: string;
  // The feed's icon (or a same-sized placeholder while it's unknown), rendered before the name.
  feedIcon: ReactNode;
  privateLockIcon: ReactNode;
  feedNameClass: string;
  feedNameClickHandler?: (ev: ReactMouseEvent) => void;
  // Whether the FeedTile is shown above the posts; drives the chevron after the name and badges.
  feedTileShown?: boolean;

  // Responsive layout
  isMobile?: boolean;
  showExpandButton?: boolean;
  onExpand?: () => void;


  // View/sort controls
  viewMode: 'full' | 'single' | 'list';
  onCycleViewMode?: () => void;
  onSetViewMode?: (mode: 'full' | 'single' | 'list') => void;
  sortNewestFirst: boolean;
  onSortChange?: (newest: boolean) => void;
  // Temporarily reveal the read posts the current view hides — the same action as the
  // "Show all posts" button at the end of the list, offered in the ⋮ menu (both the mobile
  // and the desktop/medium one) so it doesn't need scrolling to. The header action bar has
  // no room for another button. Undefined whenever the action doesn't apply (already showing
  // all, no posts, unread list).
  onShowAll?: () => void;
  // Shows the feed's FeedTile and scrolls up to it: the feed's settings (tracking, sort order,
  // limits, filter, pause) live there, so the menus only link to it.
  onFeedSetup?: () => void;

  // Mark-read controls
  allMarkedRead: boolean;
  // True when every post of the current feed is read; the header's "Mark all read"
  // button is then replaced by a "Next Feed" (or "Home") button.
  feedAllRead: boolean;
  nextUnreadFeed?: { id: string; name: string };
  onNavigateToFeed?: (feedId: string) => void;
  onMarkAllReadClick: () => void;
  onMarkOlderThanRead: (days: number) => void;
  rendererRef: RefObject<PostRendererHandle | null>;
  showMarkAllHeaderActions: boolean;
  showSmallHeaderActions: boolean;
  // Keep-All (mobile ⋮ menu entry) — mirrors the bottom Keep-All button. `keepAllLocked`
  // is true once every post is locked ("Items Locked"); onKeepAll runs the same action.
  keepAllLocked: boolean;
  onKeepAll?: () => void;
  // "Skip feed": keeps every post unread and moves on to the next unread feed (or Home).
  // Shown as an icon-only button at every width. Absent when there is nothing to skip.
  onSkipFeed?: () => void;

  // Undo for the last bulk action (Keep All / Mark All Read). When set, an "Undo" entry is
  // shown at the top of the options/⋮ menu. For a Mark-all-read undo (markAllReadUndoActive)
  // the Skip-feed toolbar button additionally morphs into an Undo button.
  onUndo?: () => void;
  undoLabel?: string;
  markAllReadUndoActive?: boolean;

  // Read-later list specific
  isReadLaterList: boolean;
  hasReadPostsInCurrentList: boolean;
  onRemoveAllReadFromReadLater: () => void;

  // Feed actions
  onLimitUnread?: () => void;
  // The feed's auto-read limits; when set, shows a checkmark on the menu entry and a
  // "max 30/7d" badge next to the feed name that opens the limits dialog.
  feedLimits?: { maxUnread?: number; maxAgeDays?: number };
  // Opens the keyword-filter dialog (pro/vip only — absent hides the menu entry). filterActive
  // marks the menu entry with a checkmark and shows the "filtered" badge next to the feed name.
  onFilterPosts?: () => void;
  filterActive?: boolean;
  // Opens the pause dialog. pausedUntil (the pause end, set while the feed is paused) marks the
  // menu entry with a checkmark and shows the "paused" badge next to the feed name.
  onPauseFeed?: () => void;
  pausedUntil?: number;
  onUnfollow?: () => void;
  // Newsletter feeds can't be restored, so the unfollow entry reads "Delete" instead.
  unfollowIsDelete?: boolean;
  onReportFeed?: () => void;
  onReportPost?: () => void;
  specialListType?: 'starred' | 'read-later' | 'all-items' | 'preview';
  // Opens the post-appearance dialog (font, size, width)
  onPostAppearance?: () => void;

  // Dropdown open/close state
  showOptionsMenu: boolean;
  setShowOptionsMenu: (v: boolean) => void;
  showMobileMenu: boolean;
  setShowMobileMenu: (v: boolean) => void;
  showMarkReadDropdown: boolean;
  setShowMarkReadDropdown: (v: boolean) => void;

  // Refs for click-outside and focus
  dropdownRef: RefObject<HTMLDivElement | null>;
  mobileMenuRef: RefObject<HTMLDivElement | null>;
  optionsMenuRef: RefObject<HTMLDivElement | null>;
  optionsMenuButtonRef: RefObject<HTMLButtonElement | null>;

  // Flash animation class applied to the options ("⋮") menu button, e.g. to point
  // the user at the menu. Currently unused; kept for ShowMeWhere-style guides.
  whereFlashClass?: string;
  // Back button before the feed name (a followed feed opened from a Home or Add Feed tile).
  onBack?: () => void;
}

export default function PostViewHeader({
  feedName, feedIcon, privateLockIcon, feedNameClass, feedNameClickHandler, feedTileShown,
  isMobile, showExpandButton, onExpand,
  viewMode, onCycleViewMode, onSetViewMode,
  sortNewestFirst, onSortChange,
  onShowAll, onFeedSetup,
  allMarkedRead, feedAllRead, nextUnreadFeed, onNavigateToFeed, onMarkAllReadClick, onMarkOlderThanRead, rendererRef,
  showMarkAllHeaderActions, showSmallHeaderActions,
  keepAllLocked, onKeepAll, onSkipFeed,
  onUndo, undoLabel, markAllReadUndoActive,
  isReadLaterList, hasReadPostsInCurrentList, onRemoveAllReadFromReadLater,
  onLimitUnread, feedLimits, onFilterPosts, filterActive, onPauseFeed, pausedUntil, onUnfollow, unfollowIsDelete, onReportFeed, onReportPost, specialListType, onPostAppearance,
  showOptionsMenu, setShowOptionsMenu,
  showMobileMenu, setShowMobileMenu,
  showMarkReadDropdown, setShowMarkReadDropdown,
  dropdownRef, mobileMenuRef, optionsMenuRef, optionsMenuButtonRef,
  whereFlashClass = '',
  onBack,
}: PostViewHeaderProps) {
  const isSavedList = specialListType == 'starred' || specialListType == 'read-later';
  // A single feed sets its sort order in the FeedTile; the lists (unread, starred…) have no tile.
  const sortInMenu = !!specialListType;
  // The medium-resolution header (>750px, <=1240px) renders SharedPostViewActions but,
  // unlike desktop, has no room for the "Aa" toolbar button — it lives in the ⋮ menu.
  const isMediumHeader = !isMobile && !!showExpandButton && !!onExpand;

  // At narrow widths the three "Mark read older than …" entries are consolidated into a
  // single menu entry that opens this popup with one button per time frame.
  const [markOlderOpen, setMarkOlderOpen] = useState(false);
  const markOlder = (days: number) => {
    rendererRef.current?.markOlderThanRead(days);
    onMarkOlderThanRead(days);
    setMarkOlderOpen(false);
  };
  const markOlderIcon = (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="8" cy="8" r="6.5" />
      <path d="M8 4.5V8l2.5 1.5" />
    </svg>
  );

  const undoIcon = <UndoIcon />;

  // "max 30/7d" / "filtered" / "paused" badges next to the feed name; clicking one opens its editor.
  // The mobile header has no room for their text: icons only, and a click toggles the FeedTile
  // (with the full badges), like a click on the feed name.
  const settingsBadges = <FeedSettingsBadges limits={feedLimits} filterActive={filterActive}
    onLimits={onLimitUnread} onFilter={onFilterPosts} pausedUntil={pausedUntil} onPause={onPauseFeed}
    iconOnly={isMobile} onBadgeClick={isMobile ? feedNameClickHandler : undefined} />;

  // Chevron after the name and badges: shows whether the FeedTile is open and toggles it, like a
  // click on the name.
  const tileToggle = feedNameClickHandler ? (
    <button type="button" className={`feedTileToggle flowFocussable sidewayFocus${feedTileShown ? ' open' : ''}`}
      title={feedTileShown ? 'Hide feed setup' : 'Show feed setup'} aria-expanded={!!feedTileShown}
      onClick={feedNameClickHandler}>
      <ChevronRightIcon size={16} />
    </button>
  ) : null;

  // Like the feed preview's back button. On mobile/medium it sits inside the clickable header-left
  // area, so stop the click from also toggling the sidebar.
  const backButton = onBack ? (
    <button
      className="pageHeaderBack flowFocussable sidewayFocus"
      tabIndex={0}
      title="Back"
      onClick={e => { e.stopPropagation(); onBack(); }}
      onKeyDown={getKeyHandler(() => onBack())}
    >
      <ChevronLeftIcon size={22} />
    </button>
  ) : null;
  // The header spreads its children apart (space-between), so the back button and the title are
  // grouped on the left, as in the preview header.
  const withBack = (title: ReactNode) => backButton ? <div className="previewHeaderLeft">{backButton}{title}</div> : title;

  // Small home button shown next to the hamburger on low/medium-width headers — only on the Unread
  // list, which is usually long and has no other navigation back. It lives inside the clickable
  // header-left area, so stop the click from also toggling the sidebar (the parent's onExpand).
  // Reuses the existing 'home' navigation target.
  const homeButton = onNavigateToFeed && specialListType == 'all-items' ? (
    <button
      className="postViewHomeButton flowFocussable sidewayFocus"
      tabIndex={0}
      title="Home"
      onClick={e => { e.stopPropagation(); onNavigateToFeed('home'); }}
      onKeyDown={getKeyHandler(() => onNavigateToFeed('home'))}
    >
      <HomeIcon size={20} />
    </button>
  ) : null;

  // Icon-only mark-read / next-feed / home button for the mobile header, where there's
  // no room for the labelled markReadGroup. Mirrors the higher-res logic: a checkmark to
  // "Mark all read" while the feed has unread posts, otherwise a Next-Feed arrow (or Home).
  const smallMarkReadButton = !isSavedList ? (
    feedAllRead ? (
      nextUnreadFeed ? (
        <button className="actionButton flowFocussable sidewayFocus" title={`Next feed: ${nextUnreadFeed.name}`} onClick={() => onNavigateToFeed?.(nextUnreadFeed.id)}>
          <ArrowRightIcon size={18} />
        </button>
      ) : (
        <button className="actionButton flowFocussable sidewayFocus" title="Home" onClick={() => onNavigateToFeed?.('home')}>
          <HomeIcon size={18} />
        </button>
      )
    ) : (
      <button className="actionButton flowFocussable sidewayFocus" title="Mark all as read" disabled={allMarkedRead} onClick={onMarkAllReadClick}>
        <span>✓</span>
      </button>
    )
  ) : null;

  // Icon-only "Skip feed" button, shown at every width (mobile included). After "Mark all
  // read" it becomes an Undo button — shown even once the list is empty (unread-tracking
  // feeds clear it), where onSkipFeed is absent.
  const skipFeedButton = !isSavedList ? (
    markAllReadUndoActive && onUndo ? (
      <button className="actionButton skipFeedButton flowFocussable sidewayFocus" title="Undo mark all read" onClick={onUndo}>
        {undoIcon}
      </button>
    ) : onSkipFeed ? (
      <button
        className="actionButton skipFeedButton flowFocussable sidewayFocus"
        title={`Skip feed. Keeps posts unread and goes to ${nextUnreadFeed ? `the next feed: ${nextUnreadFeed.name}` : 'Home'}`}
        onClick={onSkipFeed}
      >
        <SkipFeedIcon size={18} />
      </button>
    ) : null
  ) : null;

  // Shared options dropdown items — identical between medium and desktop variants.
  // Invoked as a plain function call (not <OptionsMenuDropdown/>) so it inlines into
  // the parent's element tree; rendering it as a component defined in this body would
  // give it a new identity each render, remounting the trigger button and dropping
  // focus (e.g. menuKeyDown's Esc-refocus of the ⋮ button).
  const OptionsMenuDropdown = () => (
    <div className="optionsMenuDropdown flowFormModal visibleFocus" onKeyDown={menuKeyDown(() => setShowOptionsMenu(false))} onBlur={menuBlur(() => setShowOptionsMenu(false))}>
      {onUndo && <>
      <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onUndo(); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon">{undoIcon}</span>
        <span className="optionsMenuLabel">{undoLabel || 'Undo'}</span>
      </button>
      <div className="optionsMenuDivider"></div>
      </>}
      {onSetViewMode && <>
      <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onSetViewMode('list'); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon"><ListIcon size={16} /></span>
        <span className="optionsMenuLabel">List</span>
        {viewMode == 'list' && <span className="optionsMenuCheck">✓</span>}
      </button>
      <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onSetViewMode('single'); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon"><SingleColumnIcon size={16} /></span>
        <span className="optionsMenuLabel">Single column</span>
        {viewMode == 'single' && <span className="optionsMenuCheck">✓</span>}
      </button>
      <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onSetViewMode('full'); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon"><TwoColumnIcon size={16} /></span>
        <span className="optionsMenuLabel">Full</span>
        {viewMode == 'full' && <span className="optionsMenuCheck">✓</span>}
      </button>
      </>}
      {isMediumHeader && onPostAppearance && <>
      <div className="optionsMenuDivider"></div>
      <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onPostAppearance(); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon postAppearanceAa">Aa</span>
        <span className="optionsMenuLabel">Text appearance…</span>
      </button>
      </>}
      {onShowAll && <div className="optionsMenuDivider"></div>}
      {onShowAll && <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onShowAll(); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon"><ShowAllIcon size={14} /></span>
        <span className="optionsMenuLabel">Show all posts</span>
      </button>}
      {sortInMenu && <div className="optionsMenuDivider"></div>}
      {sortInMenu && <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onSortChange?.(true); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon">↓</span>
        <span className="optionsMenuLabel">Newest posts first</span>
        {sortNewestFirst && <span className="optionsMenuCheck">✓</span>}
      </button>}
      {sortInMenu && <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onSortChange?.(false); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon">↑</span>
        <span className="optionsMenuLabel">Oldest posts first</span>
        {!sortNewestFirst && <span className="optionsMenuCheck">✓</span>}
      </button>}
      {onFeedSetup && <div className="optionsMenuDivider"></div>}
      {onFeedSetup && <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onFeedSetup(); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon"><SettingsIcon size={14} /></span>
        <span className="optionsMenuLabel">Feed setup…</span>
      </button>}
      {!showMarkAllHeaderActions && !isSavedList && !feedAllRead && <div className="optionsMenuDivider"></div>}
      {!showMarkAllHeaderActions && !isSavedList && isReadLaterList && hasReadPostsInCurrentList && (
        <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onRemoveAllReadFromReadLater(); setShowOptionsMenu(false); }}>
          <span className="optionsMenuIcon">✕</span>
          <span className="optionsMenuLabel">Remove All Read</span>
        </button>
      )}
      {!showMarkAllHeaderActions && !isSavedList && !feedAllRead && <button className="optionsMenuItem flowFocussable" tabIndex={0} disabled={allMarkedRead} onClick={() => { onMarkAllReadClick(); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon">✓</span>
        <span className="optionsMenuLabel">Mark all read</span>
      </button>}
      {!showMarkAllHeaderActions && !isSavedList && !feedAllRead && <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { setShowOptionsMenu(false); setMarkOlderOpen(true); }}>
        <span className="optionsMenuIcon">{markOlderIcon}</span>
        <span className="optionsMenuLabel">Mark read older than…</span>
      </button>}
      {(onReportPost || onReportFeed || onUnfollow) && <div className="optionsMenuDivider"></div>}
      {onReportPost && <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onReportPost(); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon"><FlagIcon size={16} /></span>
        <span className="optionsMenuLabel">Report this post</span>
      </button>}
      {onReportFeed && <button className="optionsMenuItem flowFocussable" tabIndex={0} onClick={() => { onReportFeed(); setShowOptionsMenu(false); }}>
        <span className="optionsMenuIcon"><FlagIcon size={16} /></span>
        <span className="optionsMenuLabel">Report this feed</span>
      </button>}
      {onUnfollow && (
        <button className="optionsMenuItem danger flowFocussable" tabIndex={0} onClick={() => { onUnfollow(); setShowOptionsMenu(false); }}>
          <span className="optionsMenuIcon"><UnfollowIcon size={16} /></span>
          <span className="optionsMenuLabel">{unfollowIsDelete ? 'Delete' : 'Unfollow'}</span>
        </button>
      )}
    </div>
  );

  // Shared action bar — identical between medium and desktop variants. Invoked as a
  // function call (not <SharedPostViewActions/>) so the action buttons keep a stable
  // DOM identity across re-renders; otherwise closing a menu re-renders this body,
  // remounts the buttons, and focus is lost instead of returning to the trigger.
  const SharedPostViewActions = () => (
    <div className="postViewActions">
      {showSmallHeaderActions && !isMediumHeader && onPostAppearance && (
        <button
          className="actionButton postAppearanceTrigger flowFocussable sidewayFocus"
          title="Text appearance"
          onClick={onPostAppearance}
        >
          <span className="postAppearanceAa">Aa</span>
        </button>
      )}
      {showSmallHeaderActions && onCycleViewMode && (
        <button
          className="actionButton compactToggle flowFocussable sidewayFocus"
          title={viewMode == 'full' ? 'Switch to single-column mode' : viewMode == 'single' ? 'Switch to list mode' : 'Switch to full mode'}
          onClick={() => onCycleViewMode()}
        >
          {viewMode == 'full' ? <TwoColumnIcon size={18} /> : viewMode == 'single' ? <SingleColumnIcon size={18} /> : <ListIcon size={18} />}
        </button>
      )}
      {skipFeedButton}
      {showMarkAllHeaderActions && !isSavedList && isReadLaterList ? (
        <div className="markReadGroup readLaterActions">
          {hasReadPostsInCurrentList && (
            <button className="actionButton markAllRead flowFocussable sidewayFocus" title="Remove read posts from Read Later" onClick={onRemoveAllReadFromReadLater}>
              <span>✕</span>
              <span className="actionButtonLabel">Remove All Read</span>
            </button>
          )}
          <button className="actionButton markAllRead flowFocussable sidewayFocus" title="Mark all as read" disabled={allMarkedRead} onClick={onMarkAllReadClick}>
            <span>✓</span>
            <span className="actionButtonLabel">Mark all read</span>
          </button>
        </div>
      ) : showMarkAllHeaderActions && !isSavedList && feedAllRead ? (
        // Offer the next feed (or Home) instead of a "Mark all read" button when the
        // feed is fully read. Same width as the mark-all button.
        <div className="markReadGroup">
          {nextUnreadFeed ? (
            <button className="actionButton markAllRead headerNavButton flowFocussable sidewayFocus" title={`Next feed: ${nextUnreadFeed.name}`} onClick={() => onNavigateToFeed?.(nextUnreadFeed.id)}>
              <ArrowRightIcon size={14} />
              <span className="actionButtonLabel">Next Feed</span>
            </button>
          ) : (
            <button className="actionButton markAllRead headerNavButton flowFocussable sidewayFocus" title="Home" onClick={() => onNavigateToFeed?.('home')}>
              <HomeIcon size={14} />
              <span className="actionButtonLabel">Home</span>
            </button>
          )}
        </div>
      ) : showMarkAllHeaderActions && !isSavedList ? (
        <div className="markReadGroup" ref={dropdownRef}>
          <button className="actionButton markAllRead flowFocussable sidewayFocus" title="Mark all as read" disabled={allMarkedRead} onClick={onMarkAllReadClick}>
            <span>✓</span>
            <span className="actionButtonLabel">Mark all read</span>
          </button>
          <button
            className="actionButton dropdownToggle flowFocussable sidewayFocus"
            title="Mark read options"
            onClick={() => setShowMarkReadDropdown(!showMarkReadDropdown)}
          >
            <DropdownIcon size={14} />
          </button>
          {showMarkReadDropdown && (
            <div className="dropdownMenu flowFormModal visibleFocus" onKeyDown={menuKeyDown(() => setShowMarkReadDropdown(false))} onBlur={menuBlur(() => setShowMarkReadDropdown(false))}>
              <button className="dropdownItem flowFocussable" tabIndex={0} onClick={() => { rendererRef.current?.markOlderThanRead(1); onMarkOlderThanRead(1); setShowMarkReadDropdown(false); }}>
                Older than one day
              </button>
              <button className="dropdownItem flowFocussable" tabIndex={0} onClick={() => { rendererRef.current?.markOlderThanRead(7); onMarkOlderThanRead(7); setShowMarkReadDropdown(false); }}>
                Older than one week
              </button>
              <button className="dropdownItem flowFocussable" tabIndex={0} onClick={() => { rendererRef.current?.markOlderThanRead(30); onMarkOlderThanRead(30); setShowMarkReadDropdown(false); }}>
                Older than one month
              </button>
            </div>
          )}
        </div>
      ) : null}
      <div className="optionsMenuContainer" ref={optionsMenuRef}>
        <button ref={optionsMenuButtonRef} className={`actionButton flowFocussable sidewayFocus ${whereFlashClass}`.trim()} onClick={() => setShowOptionsMenu(!showOptionsMenu)} title="Options">
          ⋮
        </button>
        {showOptionsMenu && OptionsMenuDropdown()}
      </div>
    </div>
  );

  return (
    <div className="postViewHeader">
      {isMobile ? (
        <>
          {/* A click anywhere here toggles the FeedTile (when the feed has one that can close), except
              on the hamburger and the feed icon, which open the sidebar as before. */}
          <div className="mobileHeaderLeft flowFocussable" tabIndex={0} onClick={tileToggle ? feedNameClickHandler : onExpand}>
            <span className="mobileHeaderMenuIcon" onClick={ev => { ev.stopPropagation(); onExpand?.(); }}><HamburgerIcon size={24} className="headerHamburger" /></span>
            {backButton}
            {homeButton}
            <span className="mobileHeaderMenuIcon" onClick={ev => { ev.stopPropagation(); onExpand?.(); }}>{feedIcon}</span>
            <h2><span className={feedNameClass} onClick={feedNameClickHandler}>{feedName || 'Unread Posts'}</span>{privateLockIcon}{settingsBadges}{tileToggle}</h2>
          </div>
          <div className="postViewActions">
            {showSmallHeaderActions && onCycleViewMode && (
              <button
                className="actionButton compactToggle flowFocussable sidewayFocus"
                title={viewMode == 'list' ? 'Switch to one-column mode' : 'Switch to list mode'}
                onClick={() => onCycleViewMode()}
              >
                {viewMode == 'list' ? <ListIcon size={18} /> : <SingleColumnIcon size={18} />}
              </button>
            )}
            {skipFeedButton}
            {smallMarkReadButton}
            <div className="mobileMenuContainer" ref={mobileMenuRef}>
              <button
                ref={optionsMenuButtonRef}
                className={`mobileMenuButton flowFocussable sidewayFocus ${whereFlashClass}`.trim()}
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                title="Menu"
              >
                ⋮
              </button>
              {showMobileMenu && (
                <div className="mobileMenuDropdown flowFormModal visibleFocus" onKeyDown={menuKeyDown(() => setShowMobileMenu(false))} onBlur={menuBlur(() => setShowMobileMenu(false))}>
                {onUndo && <>
                <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onUndo(); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon">{undoIcon}</span>
                  <span className="optionsMenuLabel">{undoLabel || 'Undo'}</span>
                </button>
                <div className="optionsMenuDivider"></div>
                </>}
                {onSetViewMode && <>
                <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onSetViewMode('list'); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon"><ListIcon size={16} /></span>
                  <span className="optionsMenuLabel">List</span>
                  {viewMode == 'list' && <span className="optionsMenuCheck">✓</span>}
                </button>
                {/* No HeadlineList at mobile width, so 'full' and 'single' are
                    identical — offer a single "one column" choice (full is shown
                    as single via the effective view mode passed from Main). */}
                <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onSetViewMode('single'); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon"><SingleColumnIcon size={16} /></span>
                  <span className="optionsMenuLabel">One column</span>
                  {viewMode != 'list' && <span className="optionsMenuCheck">✓</span>}
                </button>
                </>}
                {onPostAppearance && <>
                <div className="optionsMenuDivider"></div>
                <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onPostAppearance(); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon postAppearanceAa">Aa</span>
                  <span className="optionsMenuLabel">Text appearance…</span>
                </button>
                </>}
                {/* The mobile header has no toolbar Keep-All button, so offer it in the menu. */}
                {!isSavedList && onKeepAll && <>
                <div className="optionsMenuDivider"></div>
                <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onKeepAll(); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className={`keepAllMenuIcon${keepAllLocked ? ' locked' : ''}`} aria-hidden="true">
                      <rect x="3" y="5.5" width="10" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
                      <path d="M5.5 5.5V3.5a2.5 2.5 0 0 1 5 0v2" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                    </svg>
                  </span>
                  <span className="optionsMenuLabel">{keepAllLocked ? 'Keeping all' : 'Keep all'}</span>
                </button>
                </>}
                {!showMarkAllHeaderActions && !isSavedList && !feedAllRead && <div className="optionsMenuDivider"></div>}
                {!showMarkAllHeaderActions && !isSavedList && isReadLaterList && hasReadPostsInCurrentList && (
                  <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onRemoveAllReadFromReadLater(); setShowMobileMenu(false); }}>
                    <span className="optionsMenuIcon">✕</span>
                    <span className="optionsMenuLabel">Remove All Read</span>
                  </button>
                )}
                {!showMarkAllHeaderActions && !isSavedList && !feedAllRead && <button className="mobileMenuItem flowFocussable" tabIndex={0} disabled={allMarkedRead} onClick={() => { onMarkAllReadClick(); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon">✓</span>
                  <span className="optionsMenuLabel">Mark all read</span>
                </button>}
                {!showMarkAllHeaderActions && !isSavedList && !feedAllRead && <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { setShowMobileMenu(false); setMarkOlderOpen(true); }}>
                  <span className="optionsMenuIcon">{markOlderIcon}</span>
                  <span className="optionsMenuLabel">Mark read older than…</span>
                </button>}
                {onShowAll && <div className="optionsMenuDivider"></div>}
                {onShowAll && <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onShowAll(); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon"><ShowAllIcon size={14} /></span>
                  <span className="optionsMenuLabel">Show all posts</span>
                </button>}
                {sortInMenu && <div className="optionsMenuDivider"></div>}
                {sortInMenu && <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onSortChange?.(true); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon">↓</span>
                  <span className="optionsMenuLabel">Newest posts first</span>
                  {sortNewestFirst && <span className="optionsMenuCheck">✓</span>}
                </button>}
                {sortInMenu && <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onSortChange?.(false); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon">↑</span>
                  <span className="optionsMenuLabel">Oldest posts first</span>
                  {!sortNewestFirst && <span className="optionsMenuCheck">✓</span>}
                </button>}
                {onFeedSetup && <div className="optionsMenuDivider"></div>}
                {onFeedSetup && <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onFeedSetup(); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon"><SettingsIcon size={14} /></span>
                  <span className="optionsMenuLabel">Feed setup…</span>
                </button>}
                {/* Reporting must stay reachable at every width (DSA Art. 16 notice-and-action),
                    so the mobile menu carries the same report entries as the desktop one. */}
                {(onReportPost || onReportFeed || onUnfollow) && <div className="optionsMenuDivider"></div>}
                {onReportPost && <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onReportPost(); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon"><FlagIcon size={16} /></span>
                  <span className="optionsMenuLabel">Report this post</span>
                </button>}
                {onReportFeed && <button className="mobileMenuItem flowFocussable" tabIndex={0} onClick={() => { onReportFeed(); setShowMobileMenu(false); }}>
                  <span className="optionsMenuIcon"><FlagIcon size={16} /></span>
                  <span className="optionsMenuLabel">Report this feed</span>
                </button>}
                {onUnfollow && (
                  <button className="mobileMenuItem danger flowFocussable" tabIndex={0} onClick={() => { onUnfollow(); setShowMobileMenu(false); }}>
                    <span className="optionsMenuIcon"><UnfollowIcon size={16} /></span>
                    <span className="optionsMenuLabel">{unfollowIsDelete ? 'Delete' : 'Unfollow'}</span>
                  </button>
                )}
              </div>
              )}
            </div>
          </div>
        </>
      ) : showExpandButton && onExpand ? (
        <>
          {/* Medium resolution header (>750px, <=1240px) */}
          {/* In two-column mode this header spans the HeadlineList too, so it carries the
              hamburger in every view mode (matching the mobile header). */}
          <div className="mediumHeaderLeft flowFocussable" tabIndex={0} onClick={onExpand} onKeyDown={ev => { if (ev.key == 'Enter') { ev.preventDefault(); ev.stopPropagation(); onExpand?.(); } }}>
            <HamburgerIcon size={22} className="headerHamburger" />
            {backButton}
            {homeButton}
            {feedIcon}
            <h2><span className={feedNameClass} onClick={feedNameClickHandler}>{feedName || 'Unread Posts'}</span>{privateLockIcon}{settingsBadges}{tileToggle}</h2>
          </div>
          {SharedPostViewActions()}
        </>
      ) : (
        <>
          {/* Desktop header (>1240px) */}
          {withBack(<h2>
            {feedIcon}
            <span className={feedNameClass} onClick={feedNameClickHandler}>{feedName || 'Unread Posts'}</span>{privateLockIcon}{settingsBadges}{tileToggle}
          </h2>)}
          {SharedPostViewActions()}
        </>
      )}
      <Popup show={markOlderOpen} title="Mark read older than…" onClose={() => setMarkOlderOpen(false)} closable minWidth="260px" flowForm>
        <FlowForm>
          <div className="markOlderChoices">
            <button className="markOlderChoiceBtn flowFocussable" tabIndex={0} onClick={() => markOlder(1)} onKeyDown={getKeyHandler(() => markOlder(1))}>One day</button>
            <button className="markOlderChoiceBtn flowFocussable" tabIndex={0} onClick={() => markOlder(7)} onKeyDown={getKeyHandler(() => markOlder(7))}>One week</button>
            <button className="markOlderChoiceBtn flowFocussable" tabIndex={0} onClick={() => markOlder(30)} onKeyDown={getKeyHandler(() => markOlder(30))}>One month</button>
          </div>
        </FlowForm>
      </Popup>
    </div>
  );
}
